import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  agentIdFromSessionKey,
  useAppStore,
  type ChatTab,
  type CurrentView,
} from '../stores/appStore';
import { personalSessionKey } from '../hooks/useGateway';
import { useSettingsStore } from '../stores/settingsStore';
import { gatewayRef } from '../utils/gatewayRef';

interface TopHeaderNavProps {
  onMenuClick: () => void;
  menuLabel: string;
  onTabSelect?: (sessionKey: string) => void;
  onNewTab?: (mode: 'new' | 'sameContext') => void;
}

function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="app-header-nav-svg">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function DashboardIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="app-header-nav-svg">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function TeamIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="app-header-nav-svg">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="app-header-nav-svg">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function NewTabPopup({
  onSelect,
  onClose,
}: {
  onSelect: (mode: 'new' | 'sameContext') => void;
  onClose: () => void;
}) {
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <div ref={popupRef} className="chat-tab-popup">
      <button
        type="button"
        className="chat-tab-popup-btn"
        onClick={() => onSelect('new')}
      >
        New
      </button>
      <button
        type="button"
        className="chat-tab-popup-btn"
        onClick={() => onSelect('sameContext')}
      >
        Same Context
      </button>
    </div>
  );
}

export default function TopHeaderNav({ onMenuClick, menuLabel, onTabSelect, onNewTab }: TopHeaderNavProps) {
  const currentView = useAppStore((state) => state.currentView);
  const setCurrentView = useAppStore((state) => state.setCurrentView);
  const openSettings = useAppStore((state) => state.openSettings);
  const activeSessionKey = useAppStore((state) => state.activeSessionKey);
  const selectedAgentId = useAppStore((state) => state.selectedAgentId);
  const chatTabs = useAppStore((state) => state.chatTabs);
  const removeChatTab = useAppStore((state) => state.removeChatTab);
  const clearChatTabUnread = useAppStore((state) => state.clearChatTabUnread);
  const setActiveSessionKey = useAppStore((state) => state.setActiveSessionKey);

  const [showPopup, setShowPopup] = useState(false);

  const currentAgentId = selectedAgentId ?? (activeSessionKey ? agentIdFromSessionKey(activeSessionKey) : 'main');

  // Filter tabs for the current agent
  const agentTabs = useMemo(
    () => chatTabs.filter((t) => agentIdFromSessionKey(t.sessionKey) === currentAgentId),
    [chatTabs, currentAgentId],
  );

  const displayName = useSettingsStore((state) => state.settings.general.displayName);
  const connected = useAppStore((state) => state.connected);
  const connecting = useAppStore((state) => state.connecting);

  // Determine which session key is the "default" (not in extra tabs)
  const defaultAgentSessionKey = useMemo(() => {
    const trimmedName = displayName.trim();
    return personalSessionKey(currentAgentId, trimmedName);
  }, [currentAgentId, displayName]);

  const handleDefaultChatClick = useCallback(() => {
    setCurrentView('chat');
    if (defaultAgentSessionKey && defaultAgentSessionKey !== activeSessionKey) {
      setActiveSessionKey(defaultAgentSessionKey);
      onTabSelect?.(defaultAgentSessionKey);
    }
  }, [defaultAgentSessionKey, activeSessionKey, setCurrentView, setActiveSessionKey, onTabSelect]);

  const handleTabClick = useCallback(
    (tab: ChatTab) => {
      setCurrentView('chat');
      setActiveSessionKey(tab.sessionKey);
      clearChatTabUnread(tab.sessionKey);
      onTabSelect?.(tab.sessionKey);
    },
    [setCurrentView, setActiveSessionKey, clearChatTabUnread, onTabSelect],
  );

  const handleTabClose = useCallback(
    (e: React.MouseEvent, tab: ChatTab) => {
      e.stopPropagation();
      removeChatTab(tab.id);

      // For forked sessions (chat-* pattern), call sessions.delete so the
      // gateway can fire lifecycle hooks (e.g. session-memory summary).
      // Keeps the transcript (deleteTranscript: false) for history.
      if (/^agent:[^:]+:chat-/.test(tab.sessionKey)) {
        const client = gatewayRef.current;
        if (client?.connected) {
          client.request('sessions.delete', {
            key: tab.sessionKey,
            deleteTranscript: false,
          }).catch(() => {});
        }
      }

      // If closing the active tab, switch to default
      if (activeSessionKey === tab.sessionKey && defaultAgentSessionKey) {
        setActiveSessionKey(defaultAgentSessionKey);
        onTabSelect?.(defaultAgentSessionKey);
      }
    },
    [removeChatTab, activeSessionKey, defaultAgentSessionKey, setActiveSessionKey, onTabSelect],
  );

  const handleNewTab = useCallback(
    (mode: 'new' | 'sameContext') => {
      setShowPopup(false);
      onNewTab?.(mode);
    },
    [onNewTab],
  );

  const handleNavClick = (id: CurrentView | 'settings') => {
    if (id === 'settings') {
      openSettings();
      return;
    }
    setCurrentView(id);
  };

  const isDefaultChatActive = currentView === 'chat' && !agentTabs.some((t) => t.sessionKey === activeSessionKey);

  return (
    <header className="app-header safe-area-top">
      <div className="app-header-left">
        <button
          type="button"
          onClick={onMenuClick}
          className="app-header-menu-btn"
          aria-label={menuLabel}
          title={menuLabel}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" className="app-header-menu-svg">
            <path d="M4 7h16M4 12h16M4 17h16" />
          </svg>
        </button>
        <span className="app-header-brand">Clawlet</span>
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          marginLeft: '8px',
          padding: '2px 7px',
          borderRadius: '999px',
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.07)',
          lineHeight: 1,
        }}>
          <span style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            flexShrink: 0,
            background: connecting ? 'var(--theme-warning)' : connected ? 'var(--theme-success)' : '#ff5e6e',
            boxShadow: `0 0 6px ${connecting ? 'var(--theme-warning)' : connected ? 'var(--theme-success)' : '#ff5e6e'}`,
          }} />
          <span style={{
            fontSize: '10px',
            fontWeight: 600,
            letterSpacing: '0.06em',
            color: connecting ? 'var(--theme-warning)' : connected ? 'var(--theme-success)' : '#ff5e6e',
            userSelect: 'none',
          }}>
            {connecting ? 'CONNECTING' : connected ? 'LIVE' : 'OFFLINE'}
          </span>
        </span>
      </div>

      <nav className="app-header-nav" aria-label="Primary navigation">
        {/* + button to add new tab */}
        <div className="chat-tab-add-wrapper">
          <button
            type="button"
            className="chat-tab-add-btn"
            onClick={() => setShowPopup((prev) => !prev)}
            aria-label="New chat tab"
            title="New chat tab"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ width: '12px', height: '12px' }}>
              <path d="M12 5v14M5 12h14" />
            </svg>
          </button>
          {showPopup && (
            <NewTabPopup
              onSelect={handleNewTab}
              onClose={() => setShowPopup(false)}
            />
          )}
        </div>

        {/* Default Chat tab (no number, no close) */}
        <button
          type="button"
          onClick={handleDefaultChatClick}
          className={`app-header-nav-item${isDefaultChatActive ? ' is-active' : ''}`}
          aria-label="Chat"
          title="Chat"
        >
          <span className="app-header-nav-icon"><ChatIcon /></span>
          <span className="app-header-nav-label">Chat</span>
        </button>

        {/* Extra chat tabs */}
        {agentTabs.map((tab, idx) => {
          const isTabActive = currentView === 'chat' && activeSessionKey === tab.sessionKey;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleTabClick(tab)}
              className={`app-header-nav-item chat-tab-extra${isTabActive ? ' is-active' : ''}`}
              title={`Chat ${idx + 1}`}
            >
              <span className="chat-tab-number">{idx + 1}</span>
              {tab.hasUnread && <span className="chat-tab-unread">!</span>}
              <span
                className="chat-tab-close"
                onClick={(e) => handleTabClose(e, tab)}
                role="button"
                tabIndex={-1}
                aria-label={`Close chat ${idx + 1}`}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" style={{ width: '8px', height: '8px' }}>
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </span>
            </button>
          );
        })}

        {/* Team */}
        <button
          type="button"
          onClick={() => handleNavClick('team')}
          className={`app-header-nav-item${currentView === 'team' ? ' is-active' : ''}`}
          aria-label="Team"
          title="Team"
        >
          <span className="app-header-nav-icon"><TeamIcon /></span>
          <span className="app-header-nav-label">Team</span>
        </button>

        {/* Dashboard */}
        <button
          type="button"
          onClick={() => handleNavClick('dashboard')}
          className={`app-header-nav-item${currentView === 'dashboard' ? ' is-active' : ''}`}
          aria-label="Dashboard"
          title="Dashboard"
        >
          <span className="app-header-nav-icon"><DashboardIcon /></span>
          <span className="app-header-nav-label">Dashboard</span>
        </button>

        {/* Settings */}
        <button
          type="button"
          onClick={() => handleNavClick('settings')}
          className="app-header-nav-item"
          aria-label="Settings"
          title="Settings"
        >
          <span className="app-header-nav-icon"><SettingsIcon /></span>
          <span className="app-header-nav-label">Settings</span>
        </button>
      </nav>
    </header>
  );
}
